import { useCallback, useMemo } from 'react';

import { useListTickets } from '@givebacks/api/store';
import { ComboboxParsedItem, Group, MultiSelect, Text, Tooltip } from 'ui';
import { UseFormReturnType } from 'ui/Form';
import { IconInfo } from 'ui/Icons';

import { useAddEventDate } from './AddEventDate.context';
import { EventDateFormType } from './EventDateForm.types';

const EventDateTicketsMultiSelect = ({ form }: { form: UseFormReturnType<EventDateFormType> }) => {
  const { causeId, eventId } = useAddEventDate();
  const { data } = useListTickets({
    causeId,
    eventId,
    ticketsType: 'tickets',
    queryOptions: {
      searches: {
        type: { value: 'StoreItems::Ticket' },
        deleted: { value: false },
      },
      join: 'AND',
    },
  });

  const tickets = data?.store_items;

  const multiSelectTicketGroupsData = useMemo(() => {
    if (!tickets?.length) return [];
    const groupData: ComboboxParsedItem[] = [{ value: '*', label: 'All Tickets' }];

    const paidTickets = tickets
      ?.filter((ticket) => ticket.content.ticket_type === 'paid')
      .map((ticket) => ({
        value: ticket.uuid,
        label: `${ticket.name}`,
        disabled: form.values.selectedTickets.includes('*'),
      }));

    const freeTickets = tickets
      ?.filter((ticket) => ticket.content.ticket_type === 'free')
      .map((ticket) => ({
        value: ticket.uuid,
        label: `${ticket.name}`,
        disabled: form.values.selectedTickets.includes('*'),
      }));

    const donationTickets = tickets
      ?.filter((ticket) => ticket.content.ticket_type === 'donation')
      .map((ticket) => ({
        value: ticket.uuid,
        label: `${ticket.name}`,
        disabled: form.values.selectedTickets.includes('*'),
      }));

    if (paidTickets) {
      groupData.push({ group: 'Paid Tickets', items: paidTickets });
    }
    if (freeTickets) {
      groupData.push({ group: 'Free Tickets', items: freeTickets });
    }
    if (donationTickets) {
      groupData.push({ group: 'Donation Tickets', items: donationTickets });
    }

    return groupData;
  }, [form.values.selectedTickets, tickets]);

  const onValuesChange = useCallback((values: string[]) => {
    // If the user selects all tickets we want to update the multi-select to only show the all tickets option.
    if (values?.includes('*') && values.length > 1) {
      values = ['*'];
    }
    form.setFieldValue('selectedTickets', values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MultiSelect
      {...form.getInputProps('selectedTickets')}
      onChange={onValuesChange}
      label={
        <Group justify="space-between">
          <Text type="label">Select the ticket(s) that will be sold on this date</Text>
          <Tooltip
            label="All Selected ticket types will be available for this date. Sales start and end times are calculated based on the ticket type selection and event date."
            maw={300}
          >
            <IconInfo width={16} color="gray" />
          </Tooltip>
        </Group>
      }
      placeholder={form.values.selectedTickets.length ? undefined : 'Select one or more'}
      data={multiSelectTicketGroupsData}
      nothingFoundMessage="No tickets found"
    />
  );
};

export { EventDateTicketsMultiSelect };
